import { motion } from "framer-motion";

import { 
  EtLogo, 
  TrinityLogo 
} from "@/assets/img/imageAssets";

export default function EtTrinityCompanion() {

  // First Created - 22 Nov 2025
  // Author - Atul Pandey
  // Purpose — Et Trinity Companion Journey
  // Updated - 22 Nov 2025 - Cinematic AI-Infused Section — Elevated Transition to Trinity
  // Updated - 23 Nov 2025 - Copy refinements

  return (
    <section className="relative overflow-hidden py-32 bg-black text-white">

      {/* --- 1. Gradient Background (AI Transition) --- */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-950 to-orange-900/20"></div>

      {/* --- 2. Abstract AI Grid Overlay --- */}
      <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/hexellence.png')]"></div>

      {/* --- 3. Glowing Wave at Bottom (AI Horizon) --- */}
      <div
        className="
          pointer-events-none absolute bottom-0 left-1/2 -translate-x-1/2
          w-[120%] h-[250px]
          bg-gradient-to-t from-orange-600/20 via-orange-500/10 to-transparent
          blur-[60px]
        "
      ></div>

      {/* --- 4. Floating Sparks --- */}
      <div className="pointer-events-none absolute inset-0">
        {[...Array(250)].map((_, i) => (
          <div
            key={i}
            className="absolute w-[3px] h-[3px] bg-orange-400/25 rounded-full animate-companion-spark"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${6 + Math.random() * 3}s`,
            }}
          ></div>
        ))}
      </div>

      {/* --- 5. Content Grid --- */}
      <div className="relative z-20 max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-center">

        {/* Left Text Block */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1.1 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-orange-500 mb-6 drop-shadow-[0_0_20px_rgba(255,125,0,0.3)]">
            Why Trinity Was Created
          </h2>

          {/* Paragraph 1 */}
          <motion.p
            className="text-lg text-gray-300 leading-relaxed font-extralight mb-4"
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0, transition: { duration: 1 } }
            }}
          >
            Leaders don’t struggle with ideas as much as they struggle with the 
            <span className="text-orange-600"> chaos</span> of ideas. The random, scattered, restless flow 
            that makes clarity hard to hold and progress hard to sustain.
            <br /><br />
            They struggle with 
            <span className="text-orange-600"> clarity</span>, with 
            <span className="text-orange-600"> consistency</span>, and with the 
            <span className="text-orange-600"> daily rhythm</span> needed to turn intent into real progress.
            <br /><br />
            Advice alone cannot create change. Tools alone cannot sustain it.
          </motion.p>

          {/* Paragraph 2 */}
          <motion.p
            className="text-lg text-gray-300 leading-relaxed font-extralight mb-4"
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0, transition: { duration: 1 } }
            }}
          >
            Trinity was created to bring 
            <span className="text-orange-600"> structure</span>, 
            <span className="text-orange-600"> intelligence</span>, and 
            <span className="text-orange-600"> momentum </span> 
            into the daily actions of leaders and teams. It bridges the space 
            between what they want to achieve and how they actually execute.
          </motion.p>

          {/* Paragraph 3 */}
          <motion.p
            className="text-lg text-gray-300 leading-relaxed font-extralight"
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0, transition: { duration: 1 } }
            }}
          >
            It captures your journey. It learns your 
            <span className="text-orange-600"> patterns</span>. It aligns your 
            <span className="text-orange-600"> priorities</span>. And it helps you move with 
            <span className="text-orange-600"> purpose</span>. Every single day.
          </motion.p>




          {/* <p className="text-lg text-gray-300 leading-relaxed font-extralight mb-4">
            Leaders don’t struggle with ideas — they struggle with clarity,
            consistency, and the daily momentum required to turn intent into
            meaningful progress. Advice alone isn’t enough.
          </p>

          <p className="text-lg text-gray-300 leading-relaxed font-extralight mb-4">
            Trinity was created to bring intelligence, structure, and rhythm
            into the day-to-day actions of leaders and teams — bridging the gap
            between what they want to achieve and how they actually execute.
          </p>

          <p className="text-lg text-gray-300 leading-relaxed font-extralight">
            It captures your journey, learns your patterns, aligns your
            priorities, and helps you move with purpose — every single day.
          </p> */}
        </motion.div>

        {/* Right Visual / Image */}
        <motion.div
          className="relative rounded-xl overflow-hidden shadow-[0_0_40px_rgba(255,125,0,0.25)]"
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2 }}
        >
          {/* Glow behind image */}
          <div
            className="
              absolute inset-0 bg-gradient-to-br 
              from-orange-600/20 via-transparent to-black
              blur-[100px] z-0
            "
          ></div>

          {/* Image itself */}
          <img
            src={TrinityLogo}
            alt="Trinity Illustration"
            className="relative z-10 w-full h-auto object-cover"
          />
        </motion.div>
      </div>

      {/* --- Motion Keyframes --- */}
      <style>{`
        @keyframes companionSpark {
          0% { transform: translateY(0) scale(0.7); opacity: 0.15; }
          50% { transform: translateY(-25px) scale(1); opacity: 0.5; }
          100% { transform: translateY(0) scale(0.7); opacity: 0.15; }
        }
        .animate-companion-spark {
          animation: companionSpark linear infinite;
        }
      `}</style>

    </section>
    // <section className="py-20 bg-black text-white">
    //   <div className="max-w-6xl mx-auto px-4 grid md:grid-cols-2 gap-12 items-center">
    //     <motion.div
    //       initial={{ opacity: 0, x: -20 }}
    //       whileInView={{ opacity: 1, x: 0 }}
    //       viewport={{ once: true }}
    //     >
    //       <h2 className="text-3xl font-bold text-orange-600 mb-6">
    //         Why Trinity Was Created
    //       </h2>

    //       <p className="text-gray-300 text-lg leading-relaxed">
    //         Leaders don’t just need advice — they need a way to embed clarity into
    //         daily execution. Trinity bridges thinking → behavior → performance…
    //       </p>
    //     </motion.div>

    //     <motion.div
    //       className="rounded-xl overflow-hidden shadow-lg"
    //       initial={{ opacity: 0, x: 20 }}
    //       whileInView={{ opacity: 1, x: 0 }}
    //       viewport={{ once: true }}
    //     >
    //       <img
    //         src={TrinityLogo}
    //         alt="Trinity Illustration"
    //         className="w-full h-auto object-cover"
    //       />
    //     </motion.div>
    //   </div>
    // </section>
  );
}